import { RefreshCw, AlertCircle, Check } from "lucide-react";
import PropTypes from 'prop-types';


/**
 * A visual indicator that shows sync status for an entity.
 * 
 * States:
 * - syncing: Yellow spinning refresh icon
 * - failed: Red alert icon with error tooltip
 * - synced: Optional green checkmark (brief)
 * 
 * Props:
 * - status: 'syncing' | 'failed' | 'synced' | null
 * - error: Error message to show in tooltip (for failed state)
 * - show: Legacy prop - if true, shows syncing state
 */
export default function SyncIndicator({ show, status, error }) {
    // Legacy support: if show is true, treat as syncing
    const effectiveStatus = status || (show ? 'syncing' : null);

    if (!effectiveStatus) return null;

    if (effectiveStatus === 'syncing') {
        return (
            <span
                className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full"
                title="Pending sync to server"
            >
                <RefreshCw size={12} className="animate-spin" />
                Syncing...
            </span>
        );
    }

    if (effectiveStatus === 'failed') {
        return (
            <span
                className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full cursor-help"
                title={error || "Sync failed"}
            >
                <AlertCircle size={12} />
                Sync failed
            </span>
        );
    }

    if (effectiveStatus === 'synced') {
        return (
            <span
                className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full"
                title="Synced"
            >
                <Check size={12} />
                Synced
            </span>
        );
    }

    return null;
}

SyncIndicator.propTypes = {
    show: PropTypes.bool,
    status: PropTypes.string,
    error: PropTypes.string
};

/**
 * Compact sync status badge for use in lists
 */
export function SyncStatusBadge({ synced, syncStatus, syncError }) {
    if (syncStatus === 'failed') {
        return (
            <span
                className="inline-flex items-center text-red-500 cursor-help"
                title={syncError || "Sync failed - changes not saved to server"}
            >
                <AlertCircle size={14} />
            </span>
        );
    }

    if (!synced) {
        return (
            <span
                className="inline-flex items-center text-amber-500"
                title="Pending sync"
            >
                <RefreshCw size={14} className="animate-spin" />
            </span>
        );
    }

    return null;
}

SyncStatusBadge.propTypes = {
    synced: PropTypes.bool,
    syncStatus: PropTypes.string,
    syncError: PropTypes.string
};
